import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { compileAllThemes, tokens } from '@redhand/ui';
import { router } from './router.js';
import { initOtel } from './lib/otel.js';
import { watchInstallPrompt } from './lib/pwa.js';
import { UpdateBanner } from './components/update-banner.js';
import {
  DEFAULT_THEME,
  applyFontSize,
  applyTheme,
  readFontSizePreference,
  readThemePreference,
  watchSystemScheme,
} from './stores/session.js';
import '@redhand/ui/kit.css';
// Krok C2: MapPreview se presunul do kitu (tsc neumi import CSS), styl
// leafletu si proto nacita aplikace.
import 'leaflet/dist/leaflet.css';
// Krok CRM3: kalendář makléře (@event-calendar/core). Knihovna si část
// stylů skládá sama, ale její sazebník je bundlovaný CSS - musí se tu
// načíst, jinak by kalendář byl bez rozvržení.
import '@event-calendar/core/index.css';
// Krok UXR1: tokeny a sazba podle schvaleneho mockupu admin-v2 (UXM1).
// Poradi je zamerne: kit tema -> dopsane tokeny -> sazba -> stare styly,
// aby `--brand-*` z `index.css` uz cetly akcent tematu, ne vlastni cervenou.
import './styles/tokens-admin-v2.css';
import './styles/admin-v2.css';
// Krok UXV4a: sazba sdilenych komponent UX v4 (`components/ux-v4.tsx`).
import './styles/ux-v4.css';
// Krok GRAF2: mrizka a karty obrazovky Prehled (`components/overview-board.tsx`).
import './styles/overview.css';
import './index.css';

initOtel();

/*
 * Krok MOB1 bod 5 — nabidka instalace na plochu chodi z prohlizece JEDNOU a
 * driv, nez se vykresli Nastaveni; odchytit ji proto musime hned pri startu.
 * Registraci service workeru dela `UpdateBanner` (potrebuje React stav na
 * pruh „nová verze"), aby obe veci nezily ve dvou kopiich.
 */
watchInstallPrompt();

// Kit temata (AppShell, OrgSwitcher): CSS vars z theme compileru, REOS tema.
const themeStyle = document.createElement('style');
themeStyle.textContent = compileAllThemes();
document.head.appendChild(themeStyle);
document.documentElement.dataset['theme'] = 'reos';
// REDREAL zlata z kit tokenu — jen dekorativni akcent (AA: na karte < 4.5:1,
// proto neni theme accent), pouziva ji index.css (procesni kroky, zvyrazneni).
document.documentElement.style.setProperty('--reos-brand-gold', tokens.color.accent.redreal);
// Krok UXR4: velikost pisma MUSI byt na <html> pred prvnim vykreslenim,
// jinak by obrazovka po nactení preference poskocila. Synchronni modulovy
// skript bezi pred `createRoot(...).render(...)` nize — stejny vzor jako
// `data-theme` o radek vys. Zadny inline <script> v `index.html`: produkcni
// CSP (`ops/deploy-preview.sh` / `vite.config.ts` `preview.headers`) ma
// `script-src 'self'` BEZ `'unsafe-inline'`.
//
// Krok UXV4p bod 3: volba uz zije U UZIVATELE NA SERVERU; `localStorage` je
// KES prave pro tenhle okamzik (odpoved `/api/me` prijde az po prvnim
// vykresleni, takze bez kese by obrazovka blikla). Srovnani se serverem dela
// `applyServerUiPreferences` v `ensureSession`.
applyFontSize(readFontSizePreference() ?? 'normal');
// Krok UXV4a: TOTEZ pro svetly/tmavy vzhled (UX v4 § Svetly a tmavy vzhled:
// „bez bliknuti pri startu").
// Krok WEBN3 (zadani bod 1) + GRAF1 (bod 7): vychozi je pevny rezim, ne
// „podle systému", a od GRAF1 je to TMAVY — `DEFAULT_THEME` je tentyz zdroj,
// jaky zna server (`DEFAULT_USER_UI_PREFERENCES`) i `index.html`.
const themePreference = readThemePreference() ?? DEFAULT_THEME;
applyTheme(themePreference);
// Kdyz si uzivatel volbu „Podle systému" VYBERE, musi se admin prepnout i
// tehdy, kdyz si system prepne vzhled za behu (vecer). Pri jine volbe se
// neposlouchá nic.
if (themePreference === 'system') {
  watchSystemScheme(() => {
    if ((readThemePreference() ?? DEFAULT_THEME) === 'system') applyTheme('system');
  });
}

const queryClient = new QueryClient();

const el = document.getElementById('root');
if (!el) throw new Error('Missing #root');
createRoot(el).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Pruh „nová verze" stoji NAD routerem — nova verze se muze objevit na
          kazde obrazovce, vcetne prihlaseni (krok MOB1 bod 2). */}
      <UpdateBanner />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
);
